import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  BookHeart,
  CalendarDays,
  Check,
  Cloud,
  CloudMoon,
  Edit3,
  Loader2,
  LockKeyhole,
  MoonStar,
  PenLine,
  Plus,
  Save,
  Sparkles,
  Stars,
  Trash2,
} from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { DREAM_EMOTIONS, DREAM_SYMBOLS, getDreamOption, type DreamVisualOption } from "@/lib/dream-options";
import { useSleepStore } from "@/lib/use-sleep-store";
import { withMemberGate } from "@/lib/with-member-gate";

type DreamReflection = {
  id: string;
  title: string;
  body: string;
  createdAt: string;
  updatedAt: string;
};

type DreamEntry = {
  id: string;
  date: string;
  title: string;
  description: string;
  emotions: string[];
  people: string;
  locations: string;
  symbols: string[];
  themes: string[];
  reflection: string;
  reflections?: DreamReflection[];
};

const searchSchema = z.object({ dream: z.string().optional().catch(undefined) });
const prompts = [
  "What part of this dream is still with me?",
  "What did I need, want or protect in this dream?",
  "Does anything here connect with my life right now?",
  "What would I like to offer the version of me in this dream?",
];

const uid = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

export const Route = createFileRoute("/dream-reflections")({
  validateSearch: (search) => searchSchema.parse(search),
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Dream Reflections — Recovery Trauma™" },
      { name: "description", content: "A private space for longer reflections linked to your saved dreams." },
      { property: "og:title", content: "Dream Reflections — Recovery Trauma™" },
      { property: "og:description", content: "A private space for longer reflections linked to your saved dreams." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: withMemberGate(DreamReflectionsPage, "Dream Reflections"),
});

function DreamReflectionsPage() {
  const { dream: requestedDreamId } = Route.useSearch();
  const { items: dreams, commit, ready } = useSleepStore<DreamEntry>("dream");
  const [selectedId, setSelectedId] = useState(requestedDreamId ?? "");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!selectedId && dreams[0]) setSelectedId(dreams[0].id);
  }, [dreams, selectedId]);

  const selected = useMemo(
    () => dreams.find((dream) => dream.id === selectedId) ?? dreams[0],
    [dreams, selectedId],
  );
  const reflections = selected?.reflections ?? [];

  function resetEditor() {
    setEditingId(null);
    setTitle("");
    setBody("");
    setSaved(false);
  }

  function chooseDream(id: string) {
    setSelectedId(id);
    resetEditor();
  }

  function saveReflection(event: React.FormEvent) {
    event.preventDefault();
    if (!selected || !body.trim()) return;
    const now = new Date().toISOString();
    const nextReflection: DreamReflection = {
      id: editingId ?? uid(),
      title: title.trim(),
      body: body.trim(),
      createdAt: reflections.find((item) => item.id === editingId)?.createdAt ?? now,
      updatedAt: now,
    };
    const nextReflections = editingId
      ? reflections.map((item) => item.id === editingId ? nextReflection : item)
      : [nextReflection, ...reflections];
    commit(dreams.map((dream) => dream.id === selected.id ? { ...dream, reflections: nextReflections } : dream));
    setEditingId(null);
    setTitle("");
    setBody("");
    setSaved(true);
  }

  function editReflection(reflection: DreamReflection) {
    setEditingId(reflection.id);
    setTitle(reflection.title);
    setBody(reflection.body);
    setSaved(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function deleteReflection(id: string) {
    if (!selected) return;
    commit(dreams.map((dream) => dream.id === selected.id
      ? { ...dream, reflections: reflections.filter((item) => item.id !== id) }
      : dream));
    if (editingId === id) resetEditor();
  }

  if (!ready && dreams.length === 0) {
    return <div className="grid min-h-[55vh] place-items-center"><Loader2 className="h-7 w-7 animate-spin text-[var(--gold-deep)]" aria-label="Loading saved dreams" /></div>;
  }

  return (
    <main className="min-h-dvh bg-background pb-[calc(env(safe-area-inset-bottom)+6rem)] lg:pb-16">
      <div className="mx-auto w-full max-w-5xl px-4 pb-8 pt-4 sm:px-6 sm:pt-8">
        <header className="relative overflow-hidden border-b border-gold/30 px-2 pb-7 pt-4 text-center sm:pb-9">
          <Cloud className="absolute start-2 top-8 h-9 w-9 text-muted-foreground/25" aria-hidden />
          <Stars className="absolute end-3 top-3 h-8 w-8 text-[var(--gold-deep)]/55" aria-hidden />
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-full border border-gold/40 bg-[var(--qa-gold-tint)] text-[var(--gold-deep)] shadow-sm">
            <CloudMoon className="h-8 w-8" aria-hidden />
          </span>
          <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-[var(--gold-deep)]">Your private dream space</p>
          <h1 className="mt-1 font-display text-3xl text-foreground sm:text-4xl">Dream Reflections</h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            Stay with one saved dream and write whatever feels meaningful, in your own words and at your own pace.
          </p>
        </header>

        {dreams.length === 0 ? (
          <section className="mx-auto mt-8 max-w-xl border-y border-gold/25 py-10 text-center">
            <BookHeart className="mx-auto h-9 w-9 text-[var(--gold-deep)]" aria-hidden />
            <h2 className="mt-3 font-display text-2xl text-foreground">Your dream archive is empty</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Save a dream first, then return here to write a longer reflection.</p>
            <Button asChild variant="gold" className="mt-5 rounded-full"><Link to="/sleep-dreams">Open Dream Journal</Link></Button>
          </section>
        ) : (
          <div className="mt-7 grid gap-7 lg:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] lg:items-start">
            <section aria-labelledby="reflection-dream-heading">
              <h2 id="reflection-dream-heading" className="font-display text-xl text-foreground">Choose a saved dream</h2>
              <div className="mt-3 space-y-2">
                {dreams.map((dream) => {
                  const active = selected?.id === dream.id;
                  return (
                    <Button key={dream.id} type="button" variant="outline" aria-pressed={active} onClick={() => chooseDream(dream.id)} className={`h-auto w-full justify-start whitespace-normal rounded-2xl px-4 py-3 text-start ${active ? "border-gold bg-gold/15 ring-2 ring-gold ring-offset-2 ring-offset-background" : "border-border/70 bg-card"}`}>
                      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${active ? "bg-gold/25 text-[var(--gold-deep)]" : "bg-muted text-muted-foreground"}`}>{active ? <Check className="h-5 w-5" /> : <MoonStar className="h-5 w-5" />}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-display text-base text-foreground">{dream.title || "Untitled dream"}</span>
                        <span className="mt-0.5 flex items-center gap-1 text-xs font-normal text-muted-foreground"><CalendarDays className="h-3 w-3" /> {dream.date}</span>
                      </span>
                    </Button>
                  );
                })}
              </div>
            </section>

            {selected ? (
              <div className="space-y-7">
                <section className="border-y border-gold/30 bg-[var(--qa-gold-tint)]/40 px-1 py-5 sm:px-5">
                  <p className="text-xs text-muted-foreground">{selected.date}</p>
                  <h2 className="mt-1 font-display text-2xl text-foreground">{selected.title || "Untitled dream"}</h2>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-foreground/80">{selected.description}</p>
                  <VisualTags labels={selected.emotions} options={DREAM_EMOTIONS} />
                  <VisualTags labels={selected.symbols} options={DREAM_SYMBOLS} />
                </section>

                <form onSubmit={saveReflection} className="space-y-5" aria-labelledby="write-reflection-heading">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[var(--gold-deep)]">{editingId ? "Editing your words" : "A page for what remains"}</p>
                    <h2 id="write-reflection-heading" className="mt-1 font-display text-2xl text-foreground">{editingId ? "Edit reflection" : "Write a reflection"}</h2>
                  </div>

                  <div>
                    <label htmlFor="reflection-title" className="text-sm font-medium text-foreground">Reflection title <span className="font-normal text-muted-foreground">(optional)</span></label>
                    <input id="reflection-title" value={title} onChange={(event) => { setTitle(event.target.value); setSaved(false); }} maxLength={160} placeholder="What stayed with me…" className="mt-1.5 min-h-11 w-full rounded-xl border border-border bg-card px-4 py-2.5 text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring" />
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Gentle prompts</p>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      {prompts.map((prompt) => (
                        <Button key={prompt} type="button" variant="outline" onClick={() => { setBody((current) => current ? `${current}\n\n${prompt}\n` : `${prompt}\n`); setSaved(false); }} className="h-auto min-h-14 items-start justify-start gap-2 whitespace-normal rounded-xl border-gold/20 bg-[var(--qa-gold-tint)]/35 px-3 py-2.5 text-start text-xs font-normal leading-relaxed text-foreground/85 hover:bg-gold/10">
                          <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--gold-deep)]" aria-hidden /><span>{prompt}</span>
                        </Button>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">Use a prompt only if it feels helpful.</p>
                  </div>

                  <div>
                    <label htmlFor="reflection-body" className="text-sm font-medium text-foreground">Your reflection</label>
                    <div className="relative mt-1.5 overflow-hidden rounded-2xl border border-gold/30 bg-card shadow-inner focus-within:ring-2 focus-within:ring-ring">
                      <PenLine className="pointer-events-none absolute end-4 top-4 h-4 w-4 text-[var(--gold-deep)]/55" aria-hidden />
                      <textarea id="reflection-body" rows={14} maxLength={12000} required value={body} onChange={(event) => { setBody(event.target.value); setSaved(false); }} placeholder="I notice…" className="min-h-80 w-full resize-y bg-transparent px-4 py-3 pe-11 text-base leading-8 text-foreground outline-none placeholder:text-muted-foreground/65" style={{ backgroundImage: "repeating-linear-gradient(transparent, transparent 31px, color-mix(in oklab, var(--gold) 18%, transparent) 32px)" }} />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Button type="submit" variant="gold" disabled={!body.trim()} className="w-full rounded-full sm:w-auto sm:px-7"><Save className="h-4 w-4" /> {editingId ? "Save changes" : "Save reflection"}</Button>
                    {editingId ? <Button type="button" variant="ghost" onClick={resetEditor} className="rounded-full">Cancel editing</Button> : null}
                    {saved ? <p role="status" className="flex items-center justify-center gap-1.5 text-sm text-[var(--gold-deep)]"><Check className="h-4 w-4" /> Reflection saved</p> : null}
                  </div>
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><LockKeyhole className="h-3.5 w-3.5" /> Private and saved to your account. Nothing here is analysed automatically.</p>
                </form>

                {reflections.length > 0 ? (
                  <section aria-labelledby="saved-reflections-heading" className="border-t border-gold/30 pt-7">
                    <h2 id="saved-reflections-heading" className="font-display text-2xl text-foreground">Saved reflections</h2>
                    <div className="mt-4 space-y-4">
                      {reflections.map((reflection) => (
                        <article key={reflection.id} className="rounded-2xl border border-border/70 bg-card p-5 shadow-sm">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-xs text-muted-foreground">{new Date(reflection.updatedAt).toLocaleDateString()}</p>
                              <h3 className="mt-1 font-display text-lg text-foreground">{reflection.title || "Untitled reflection"}</h3>
                            </div>
                            <div className="flex shrink-0 gap-1">
                              <Button type="button" variant="ghost" size="icon" aria-label={`Edit ${reflection.title || "reflection"}`} onClick={() => editReflection(reflection)}><Edit3 className="h-4 w-4" /></Button>
                              <Button type="button" variant="ghost" size="icon" aria-label={`Delete ${reflection.title || "reflection"}`} onClick={() => deleteReflection(reflection.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </div>
                          <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-foreground/85">{reflection.body}</p>
                        </article>
                      ))}
                    </div>
                  </section>
                ) : (
                  <div className="border-t border-dashed border-gold/30 pt-6 text-center text-sm text-muted-foreground"><Plus className="mx-auto mb-2 h-5 w-5 text-[var(--gold-deep)]" />Your saved reflections for this dream will appear here.</div>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </main>
  );
}

function VisualTags({ labels, options }: { labels: string[]; options: DreamVisualOption[] }) {
  if (labels.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {labels.map((label) => {
        const option = getDreamOption(label, options);
        const Icon = option?.icon ?? Sparkles;
        return <span key={label} className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 bg-card/75 px-2.5 py-1 text-xs text-foreground"><Icon className="h-3.5 w-3.5 text-[var(--gold-deep)]" />{label}</span>;
      })}
    </div>
  );
}